import React, { useEffect, useCallback, useContext, useMemo, useState } from "react";
import { Box, Button, Popover, PopoverAnchor, PopoverBody, PopoverContent, PopoverFooter } from "@chakra-ui/react";
import { Select } from "../../Atoms";

import { DATE_OPTIONS, DATE_RANGES, DEFAULT_DATE } from "../../../Library/constants";
import { StoreContext } from "../../../Library/contexts";
import { getLocalisedDate, getDateEnd, getDateStart, getISODate } from "../../../Library/datetime";

import { CalendarIcon } from "../../../Assets/images/svgs";
import "./styles.css";

const DatePicker = ({customMaxDate, onClose, onSave, showDefaultRanges = true, showToday = false}) => {
  const { filters, setFilters } = useContext(StoreContext);
  const [fromTs, setFromTs] = useState(showDefaultRanges ? Date.now() : (showToday ? new Date().setHours(0, 0, 0, 0) : DATE_RANGES[DATE_OPTIONS.YESTERDAY].from));
  const [toTs, setToTs] = useState((showDefaultRanges || showToday) ? Date.now() : DATE_RANGES[DATE_OPTIONS.YESTERDAY].to);
  const range = filters.range;
  const setRange = (r) => setFilters({ ...filters, range: r });

  const [ showModal, setShowModal ] = useState(false);

  useEffect(() => {
    if (!showDefaultRanges) return;
    const url = new URL(window.location);

    const existingParams = new URLSearchParams(url.search);
    if (range === DATE_OPTIONS.CUSTOM) {
      existingParams.set("from", DATE_RANGES[DATE_OPTIONS.CUSTOM].from);
      existingParams.set("to", DATE_RANGES[DATE_OPTIONS.CUSTOM].to);
    } else {
      existingParams.delete("from");
      existingParams.delete("to");
    }

    if (range !== DEFAULT_DATE) {
      existingParams.set("range", range);
    } else {
      existingParams.delete("range");
    }

    url.search = existingParams.toString();
    window.history.replaceState(window.history.state, "", url);
  }, [range, showDefaultRanges]);

  const openCustomPicker = useCallback(() => {
    // Setting Currently Selected Range as Default for Custom Picker
    DATE_RANGES[DATE_OPTIONS.CUSTOM] = DATE_RANGES[range];
    setShowModal(true);
    setFromTs(DATE_RANGES[range].from === 1 ? getDateStart() : DATE_RANGES[range].from);
    setToTs(DATE_RANGES[range].to);
  }, [ range ]);

  const closeCustomPicker = (save) => {
    if (save) {
      if (showDefaultRanges) {
        DATE_RANGES[DATE_OPTIONS.CUSTOM] = { from: fromTs, to: toTs };
        setRange(DATE_OPTIONS.CUSTOM);
        const url = new URL(window.location);
        url.searchParams.set("from", fromTs);
        url.searchParams.set("to", toTs);
        window.history.replaceState(window.history.state, "", url);
      }
      else if (onSave) {
        onSave({ fromTs, toTs });
      }
    }
    setShowModal(false);
    if (onClose) onClose();
  };

  const options = useMemo(() => {
    if (!showDefaultRanges) return [];
    return ([
      { value: DATE_OPTIONS.YESTERDAY,     label: "Yesterday"    },
      { value: DATE_OPTIONS.LAST_7_DAYS,   label: "Last 7 Days"  },
      { value: DATE_OPTIONS.LAST_30_DAYS,  label: "Last 30 Days" },
      { value: DATE_OPTIONS.THIS_YEAR,     label: "This Year"    },
      { value: DATE_OPTIONS.LIFETIME,      label: "Lifetime"     },
      { value: DATE_OPTIONS.CUSTOM,        label: "Custom Range", onClick: openCustomPicker },
    ]);
  }, [openCustomPicker, showDefaultRanges]);

  const getCalendar = () => {
    const handleClick = (e) => {
      e.preventDefault();
      if (range === DATE_OPTIONS.CUSTOM) openCustomPicker();
    };

    return <CalendarIcon onClick={handleClick} className="date-picker-icon" />;
  };

  const getLabel = () => {
    if (showDefaultRanges) {
      if (range === DATE_OPTIONS.CUSTOM) {
        const range = DATE_RANGES[DATE_OPTIONS.CUSTOM];
        if (range.from === 1) range.from = getDateStart();
        return (
          <div>
            <span>{getLocalisedDate(range.from, true)}</span>
            <span style={{ padding: "0 8px" }}>{"-"}</span>
            <span>{getLocalisedDate(range.to, true)}</span>
          </div>
        );
      }
    } else {
      return (
        <div className='date-range-cnt-vals'>
          <span>{getLocalisedDate(fromTs, true)}</span>
          <span style={{ padding: "0 8px" }}>-</span>
          <span>{getLocalisedDate(toTs, true)}</span>
        </div>
      );
    }
  };

  const onChangeFrom = (e) => setFromTs(getDateStart(new Date(e.target.value).getTime()));
  const onChangeTo = (e) => setToTs(getDateEnd(new Date(e.target.value).getTime()));

  return (
    <Box>
      <Popover isOpen={showModal} onClose={() => closeCustomPicker(false)} isLazy>
        <PopoverAnchor>
          <Box onClick={!showDefaultRanges ? () => setShowModal(true) : undefined}>
            <Select
              selected={showDefaultRanges && range}
              options={options}
              onChange={showDefaultRanges && setRange}
              label={getLabel()}
              icon={getCalendar()}
              width={350}
            />
          </Box>
        </PopoverAnchor>

        <PopoverContent className="panel-date-modal" width={350}>
          <PopoverBody>
            <div className="date-modal-header">Select Date Range</div>
            <div className="panel-custom-date">
              <input onClick={(e) => e.currentTarget.showPicker()} type="date" value={getISODate(fromTs)} onChange={onChangeFrom} max={customMaxDate ? customMaxDate : ""}/>
              <input onClick={(e) => e.currentTarget.showPicker()} type="date" value={getISODate(toTs)} onChange={onChangeTo} max={customMaxDate ? customMaxDate : ""}/>
            </div>
          </PopoverBody>
          <PopoverFooter className="date-modal-footer">
            {!showDefaultRanges && <Button variant="link" onClick={() => closeCustomPicker(false)}>
              Close
            </Button>}
            <Button variant="link" onClick={() => closeCustomPicker(true)}>
              {showDefaultRanges ? "Apply" : "Save"}
            </Button>
          </PopoverFooter>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default DatePicker;
